import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { PreferenceCategory } from 'voyager';

import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { settingSelectors } from '@store/setting/selectors';

import type { Property } from 'shared-types/Property';

export function useDiningPreferences() {
  const { t } = useTranslations();
  const property = useSelector(entitySelectors.getProperty) as Property | undefined;
  const language =
    (useSelector(settingSelectors.getUserLanguage) as string | undefined) ??
    property?.defaultLanguage ??
    'en';

  const { data: tablesPage } = useGetTablesPage(property?.id ?? 0, language);
  const config = tablesPage as
    | { dietaryRequirements?: string[]; celebrationOptions?: string[] }
    | null
    | undefined;
  const dietaryRequirements = Array.isArray(config?.dietaryRequirements)
    ? config.dietaryRequirements
    : [];
  const celebrationOptions = Array.isArray(config?.celebrationOptions)
    ? config.celebrationOptions
    : [];

  const [notes, setNotes] = useState('');
  const [selectedByCategory, setSelectedByCategory] = useState<Record<string, string[]>>({});
  const [otherByCategory, setOtherByCategory] = useState<Record<string, string>>({});

  const onPreferenceOptionToggle = useCallback(
    (id: string, option: string) =>
      setSelectedByCategory((prev) => {
        const current = prev[id] ?? [];
        return {
          ...prev,
          [id]: current.includes(option)
            ? current.filter((value) => value !== option)
            : [...current, option],
        };
      }),
    []
  );

  const onPreferenceOtherChange = useCallback(
    (id: string, value: string) => setOtherByCategory((prev) => ({ ...prev, [id]: value })),
    []
  );

  const onPreferenceFreeTextChange = useCallback(
    (_id: string, value: string) => setNotes(value),
    []
  );

  const preferenceCategories = useMemo<PreferenceCategory[]>(() => {
    const configuredCategories: PreferenceCategory[] = [];

    if (dietaryRequirements.length > 0) {
      configuredCategories.push({
        id: 'dietary',
        label: t('dining.addDietary'),
        options: dietaryRequirements,
        selectedOptions: selectedByCategory.dietary ?? [],
        otherValue: otherByCategory.dietary ?? '',
      });
    }

    if (celebrationOptions.length > 0) {
      configuredCategories.push({
        id: 'occasion',
        label: t('dining.addOccasion'),
        options: celebrationOptions,
        selectedOptions: selectedByCategory.occasion ?? [],
        otherValue: otherByCategory.occasion ?? '',
      });
    }

    if (configuredCategories.length === 0) {
      return [];
    }

    return [
      {
        id: 'notes',
        label: t('dining.addNotes'),
        isFreeText: true,
        freeTextPlaceholder: t('dining.notesPlaceholder'),
        freeTextValue: notes,
      },
      ...configuredCategories,
    ];
  }, [t, notes, dietaryRequirements, celebrationOptions, selectedByCategory, otherByCategory]);

  return {
    onPreferenceOptionToggle,
    onPreferenceOtherChange,
    onPreferenceFreeTextChange,
    preferenceCategories,
    values: {
      note: notes,
      dietaryRequirements: selectedByCategory.dietary ?? [],
      celebrations: selectedByCategory.occasion ?? [],
    },
  };
}
